import Dropdown from './index';
import {
  MenuDivider,
  MenuItem,
  MenuLink,
  PopoverButton,
  PopoverMenu,
  UserNameList,
} from '../MainHeader/styles';
import Avatar from '../Avatar';
import { black, darkGray } from '../../utils/style/colors';
import {
  ProfileDropDownHeader,
  ProfileDropDownUserName,
  RolesContainer,
  RolesHeader,
  ThemeModeContainer,
} from '../Sidebar/styles';
import React, { useEffect } from 'react';
import i18n from '../../i18n';
import messages from '../Sidebar/messages';
import Pill from '../Pill';
import BorderBadge from '../Badge/BorderBadge';
import Icon from '../Icon';
import { useTranslation } from 'react-i18next';
import { getDomainAndPort, isOutPatientDomain } from '../../api/Utils';
import {
  IThisUser,
  IThisUserState,
  IUserFlag,
} from '../../store/thisUser/thisUserSlice';
import { ICompanyRole } from '../../store/companies/companiesTypes';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTour } from '@reactour/tour';
import { addTourStep } from '../../context/Tour/TourWrapper';
import DarkModeClass from 'hooks/DarkModeClass';

interface IProps {
  direction: 'left' | 'right' | 'top' | 'bottom';
  user: IThisUser | IThisUserState;
  companyId?: number;
  isHidden: boolean;
  enableMobileViewMode?: boolean;
  isWebda?: boolean;
  isThemeEnabled?: boolean;
}

const roleOrders = {
  Compound: 1,
  InProject: 2,
  Post: 3,
  District: 4,
};

const BadgeProfileDropdown: React.FC<IProps> = ({
  direction,
  user,
  companyId,
  isHidden,
  enableMobileViewMode = true,
  isWebda = false,
  isThemeEnabled = false,
}) => {
  const userBadgeTour = [
    {
      selector: '.userBadgeProfile',
      content: `${i18n.t('sidebar', { ns: 'general' })}`,
      category: 'userBadgeProfile',
    },
  ];

  const thisUser = useSelector((state: RootState) => state.thisUser);

  const company = useSelector((state: RootState) =>
    companyId ? state.companies.data[companyId] : undefined
  );
  const tourFlags = useSelector(
    (state: RootState) => state.thisUser.userFlags?.['tour']
  );
  const { setSteps } = useTour();

  const employeeId = company?.members?.[Number(user?.userId)]?.employeeId;
  const companyRoles = company?.roles;

  const [showDropdown, setShowDropdown] = React.useState(false);

  const { t } = useTranslation('mainNavBar');

  const handleShowDropdown = () => {
    setShowDropdown((prevVal) => !prevVal);
  };

  const domainAndPort = getDomainAndPort();
  const isOutpatientView = isOutPatientDomain();
  const showUserId =
    domainAndPort.domainFeatures && domainAndPort.domainFeatures.showUserId;

  const userRolesIds =
    companyRoles &&
    Object.values(companyRoles)?.reduce((acc: number[], role: ICompanyRole) => {
      if (
        (Array.isArray(role?.users) ? role.users : Object.values(role?.users))
          ?.map((item: any) => item?.userId)
          ?.includes(Number(user?.userId))
      )
        acc.push(role.id);
      return acc;
    }, []);

  const sortedRoles =
    (
      userRolesIds
        ?.map((id: number) => companyRoles?.[id])
        ?.filter((it) => !!it) as ICompanyRole[]
    )
      ?.sort(
        (a: any, b: any) =>
          (a.isUnique ? 0 : (a.roleType && roleOrders[a.roleType]) || 5) -
          (a.isUnique ? 0 : (b.roleType && roleOrders[b.roleType]) || 5)
      )
      ?.reduce((acc: string[], item: ICompanyRole) => {
        acc.push(item.displayName as string);
        return acc;
      }, []) || [];

  if (user.jwtData) {
    sortedRoles.push(user.jwtData.organizationTitle);
    sortedRoles.push(user.jwtData.role);
  }

  useEffect(() => {
    if (
      !tourFlags?.find(
        (flag: IUserFlag) => flag.flagName === 'userBadgeProfile'
      ) &&
      !window.location.href.includes('/homepage') &&
      !isHidden &&
      thisUser.userId
    ) {
      addTourStep(setSteps, userBadgeTour);
    }
  }, [tourFlags]);

  return (
    <ProfileDropDownHeader className="userBadgeProfile">
      <Dropdown
        curveBorder={true}
        marginToTarget={10}
        placement={`${direction === 'bottom' ? 'bottom-start' : direction === 'left' ? 'left-end' : direction === 'top' ? 'top-start' : 'auto'}`}
        width={200}
        show={showDropdown}
        onShowChange={handleShowDropdown}
        target={
          <PopoverButton style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              icon="user"
              customStyle={{
                background: `${darkGray}`,
                flexShrink: 0,
                margin: 'auto',
              }}
            />
            {!isHidden && (
              <ProfileDropDownUserName
                enableMobileViewMode={enableMobileViewMode}
              >
                <div style={{ color: black }}>
                  <b>{user.name}</b> - {employeeId}
                </div>
                <div>
                  {sortedRoles && sortedRoles?.length > 0 && (
                    <BorderBadge content={sortedRoles?.[0]} isInline={true}>
                      {sortedRoles?.[0]}
                    </BorderBadge>
                  )}
                  {sortedRoles && sortedRoles?.length > 1 ? ' , ...' : ''}
                </div>
              </ProfileDropDownUserName>
            )}
          </PopoverButton>
        }
      >
        <PopoverMenu>
          {showUserId && (
            <UserNameList>
              {user.name || messages.user}
              <Pill>
                {t('code')}
                {user.userId}
              </Pill>
            </UserNameList>
          )}
          {sortedRoles?.length > 0 && (
            <RolesContainer>
              <RolesHeader>
                {i18n.t('roles', { ns: 'profile' }) as string}
              </RolesHeader>
              {sortedRoles?.map((item: string) => {
                return (
                  <BorderBadge key={`${item}`} content={item}>
                    <MenuItem>{item}</MenuItem>
                  </BorderBadge>
                );
              })}
            </RolesContainer>
          )}
          {user.isLoggedIn && (
            <MenuLink exact={true} path="/c/profile">
              <MenuItem>
                <Icon type="user" />
                {t('profile')}
              </MenuItem>
            </MenuLink>
          )}
          {!isOutpatientView && user.isLoggedIn && (
            <MenuLink path="/c/company/list">
              <MenuItem>
                <Icon type="briefcase" />
                {t('companies')}
              </MenuItem>
            </MenuLink>
          )}
          {isThemeEnabled ? (
            <>
              <MenuDivider style={{ width: '100%' }}></MenuDivider>

              <ThemeModeContainer>
                <DarkModeClass isWebda={isWebda} />
              </ThemeModeContainer>
            </>
          ) : (
            <></>
          )}

          <MenuDivider style={{ width: '100%' }}></MenuDivider>
          {user.isLoggedIn ? (
            <MenuLink path="/logout">
              <MenuItem>
                <Icon type="log-out" />
                {t('exit')}
              </MenuItem>
            </MenuLink>
          ) : (
            <MenuLink path="/login" style={{ marginBottom: '0.5rem' }}>
              <MenuItem>
                <Icon type="log-in" />
                {t('login')}
              </MenuItem>
            </MenuLink>
          )}
        </PopoverMenu>
      </Dropdown>
    </ProfileDropDownHeader>
  );
};

export default BadgeProfileDropdown;
